export const CHECKOUT_URL = 'https://reendo-en.impultienda.ar/checkout/';

export const MOCKUP_URL =
  'https://cdn.magicpatterns.com/uploads/9tMDnBdJdm5u79R6z4LAvQ/MOCKUP_REENDO-EN.png';

export const AUTHOR_URL =
  'https://cdn.magicpatterns.com/uploads/urPCUFSDDhdKRoDSfHaNAe/VALERIA_MONTES_-_BIOMECANICA.png';

export const heroChecks = [
  '6 Actionable Clinical Volumes',
  '5 Practical Clinical Bonuses',
  'Assessment Checklists & Decision Matrices',
  'Printable Documentation Templates',
  'Instant Digital Access',
];

export const problemList = [
  'Persistent symptoms after previous root canal treatment.',
  'Radiographic findings that require further investigation.',
  'Uncertain restorative prognosis.',
  'Possible procedural complications.',
  'Cases where retreatment, surgery, monitoring, or referral may need consideration.',
  'Complex documentation and follow-up requirements.',
];

export const steps = [
  {
    key: 'ASSESS',
    title: 'Build a Complete Case Overview',
    body: "Organize the patient's relevant history, presenting concerns, previous treatment information, and initial clinical findings. Use structured intake prompts to identify missing information and establish a clear starting point.",
  },
  {
    key: 'INVESTIGATE',
    title: 'Organize Clinical & Imaging Findings',
    body: 'Document examination findings and available imaging information using a consistent assessment framework. Identify findings that may require additional evaluation or specialist interpretation.',
  },
  {
    key: 'EVALUATE',
    title: 'Identify the Factors That Influence Planning',
    body: 'Review relevant endodontic, periodontal, restorative, and patient-specific considerations. Use structured matrices to organize factors that may influence prognosis and management decisions.',
  },
  {
    key: 'COMPARE',
    title: 'Structure the Available Management Options',
    body: 'Organize the considerations associated with nonsurgical retreatment, surgical approaches, monitoring, extraction, and specialist referral when clinically appropriate. Compare options without replacing professional judgment or individualized clinical assessment.',
  },
  {
    key: 'DOCUMENT',
    title: 'Create a Clear Planning & Follow-Up Record',
    body: 'Consolidate the findings, clinical reasoning, proposed next steps, referral considerations, and follow-up arrangements into one organized record.',
  },
];

export const beforeItems = [
  'Clinical information scattered across multiple notes.',
  'No consistent sequence for organizing retreatment assessments.',
  'Important planning factors reviewed without a unified checklist.',
  'Difficulty comparing management considerations in one place.',
  'Referral and follow-up information documented inconsistently.',
  'Repeatedly creating assessment notes from scratch.',
];

export const afterItems = [
  'One structured five-step assessment workflow.',
  'Organized clinical and radiographic documentation.',
  'Practical matrices for reviewing relevant planning factors.',
  'A consistent framework for comparing management options.',
  'Dedicated referral and follow-up templates.',
  'Reusable worksheets for appropriate future cases.',
];

export const volumes = [
  {
    number: '01',
    title: 'Previously Treated Tooth Assessment Blueprint™',
    subtitle: 'Build a Structured Starting Point for Every Appropriate Case',
    description:
      'Start with a consistent approach to collecting and organizing relevant case information.',
    items: [
      'Previously Treated Tooth Intake Checklist',
      'Endodontic Treatment History Worksheet',
      'Presenting Symptoms Documentation Matrix',
      'Initial Clinical Assessment Checklist',
      'Missing Information Identification Tool',
      'Case Summary Template',
    ],
    deliverable: 'A reusable initial assessment worksheet for previously treated teeth.',
  },
  {
    number: '02',
    title: 'Endodontic Findings Organization Roadmap™',
    subtitle: 'Turn Multiple Clinical Findings Into One Organized Assessment Record',
    description:
      'Bring clinical examination findings and imaging observations into a structured documentation workflow.',
    items: [
      'Clinical Findings Recording Checklist',
      'Radiographic Observations Worksheet',
      'Symptom and Finding Correlation Matrix',
      'Additional Assessment Considerations Checklist',
      'Diagnostic Uncertainty Documentation Tool',
      'Structured Findings Summary Template',
    ],
    deliverable:
      'A consolidated clinical findings worksheet designed to support professional assessment.',
  },
  {
    number: '03',
    title: 'Retreatment Planning Factor Matrix™',
    subtitle: 'Organize the Factors That Matter Before Comparing Treatment Options',
    description:
      'Evaluate the information relevant to treatment planning without overlooking restorative, periodontal, endodontic, or patient-specific considerations.',
    items: [
      'Endodontic Planning Factors Checklist',
      'Restorability Assessment Documentation Matrix',
      'Periodontal Considerations Worksheet',
      'Previous Treatment Complexity Review',
      'Patient-Specific Planning Factors Template',
      'Integrated Planning Summary Matrix',
    ],
    deliverable:
      'A structured planning-factor matrix for documenting relevant clinical considerations.',
  },
  {
    number: '04',
    title: 'Endodontic Management Options Decision Map™',
    subtitle: 'Compare Management Pathways Through a Consistent Clinical Framework',
    description:
      'Organize the considerations that may influence the selection of an appropriate management pathway.',
    items: [
      'Nonsurgical Retreatment Considerations Matrix',
      'Surgical Management Considerations Worksheet',
      'Monitoring and Reassessment Planning Template',
      'Extraction and Restorative Planning Considerations',
      'Specialist Referral Decision Checklist',
      'Shared Decision-Making Documentation Worksheet',
    ],
    deliverable:
      'A management-options comparison worksheet supporting individualized professional decision-making.',
  },
  {
    number: '05',
    title: 'Complex Retreatment Case Planning Blueprint™',
    subtitle: 'Bring Your Assessment Findings Into One Coherent Planning Record',
    description:
      'Combine the information gathered throughout the pathway into an organized case-planning workflow.',
    items: [
      'Integrated Case Review Worksheet',
      'Clinical Reasoning Documentation Framework',
      'Treatment Planning Considerations Checklist',
      'Patient Communication Documentation Template',
      'Referral Information Summary',
      'Final Planning Record Template',
    ],
    deliverable:
      'A complete clinical planning worksheet connecting assessment findings with documented next steps.',
  },
  {
    number: '06',
    title: 'Retreatment Documentation & Follow-Up System™',
    subtitle: 'Create a Repeatable Workflow for Clinical Records and Follow-Up',
    description:
      'Keep assessment records, referral information, and follow-up plans organized.',
    items: [
      'Retreatment Assessment Documentation Template',
      'Patient Discussion Recording Checklist',
      'Follow-Up Planning Worksheet',
      'Reassessment Findings Comparison Tool',
      'Referral Communication Template',
      'Case Documentation Completion Checklist',
    ],
    deliverable:
      'A reusable documentation and follow-up toolkit for endodontic retreatment assessments.',
  },
];

export const bonuses = [
  {
    number: '01',
    title: '5-Step Retreatment Assessment Quick Card™',
    value: '$19',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a60fdfb1-114f-45e8-bb94-3d8b32ca18d8.jpg',
    description:
      'A compact reference card summarizing the five stages of the ENDO RETREATMENT DECISION PATHWAY™. Designed for quick consultation when organizing an assessment.',
    items: ['Five-Step Workflow Summary', 'Key Documentation Prompts', 'Assessment Completion Checklist'],
  },
  {
    number: '02',
    title: 'Endodontic Referral Handoff Template Pack™',
    value: '$17',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f66b1690-f9c9-4ab7-ae8d-167da51cf8a6.jpg',
    description:
      'Reusable templates for organizing the information that may need to accompany a specialist referral.',
    items: [
      'Referral Summary Template',
      'Relevant Findings Checklist',
      'Imaging Information Documentation Guide',
      'Referral Communication Worksheet',
    ],
  },
  {
    number: '03',
    title: 'Retreatment Case Documentation Worksheets™',
    value: '$27',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/6d2e0210-5251-4066-b52c-0f92ca4a6ee9.jpg',
    description:
      'A collection of printable worksheets for recording assessment findings and planning considerations.',
    items: [
      'Initial Case Worksheet',
      'Findings Summary Sheet',
      'Planning Considerations Form',
      'Follow-Up Documentation Template',
    ],
  },
  {
    number: '04',
    title: 'Retreatment Management Comparison Matrix™',
    value: '$19',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0c91155c-856d-4484-9028-1bd641fc783e.jpg',
    description:
      'A structured comparison tool for organizing the considerations associated with different management pathways.',
    items: [
      'Management Options Worksheet',
      'Patient-Specific Factors Matrix',
      'Referral Considerations Checklist',
      'Shared Decision-Making Record',
    ],
  },
  {
    number: '05',
    title: 'Clinical Case Review & Audit Checklist™',
    value: '$17',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4bb3ac7c-2910-415d-9dcb-5369ea1ab9d4.jpg',
    description:
      'A practical checklist for reviewing the completeness and consistency of retreatment assessment documentation.',
    items: [
      'Assessment Record Review',
      'Documentation Completeness Checklist',
      'Planning Record Review',
      'Follow-Up Information Checklist',
    ],
  },
];

export const valueStack = [
  { label: 'Volume 01 — Previously Treated Tooth Assessment Blueprint™', value: '$39' },
  { label: 'Volume 02 — Endodontic Findings Organization Roadmap™', value: '$39' },
  { label: 'Volume 03 — Retreatment Planning Factor Matrix™', value: '$39' },
  { label: 'Volume 04 — Endodontic Management Options Decision Map™', value: '$39' },
  { label: 'Volume 05 — Complex Retreatment Case Planning Blueprint™', value: '$39' },
  { label: 'Volume 06 — Retreatment Documentation & Follow-Up System™', value: '$39' },
  { label: 'Bonus 01 — Assessment Quick Card™', value: '$19' },
  { label: 'Bonus 02 — Referral Handoff Template Pack™', value: '$17' },
  { label: 'Bonus 03 — Case Documentation Worksheets™', value: '$27' },
  { label: 'Bonus 04 — Management Comparison Matrix™', value: '$19' },
  { label: 'Bonus 05 — Case Review & Audit Checklist™', value: '$17' },
];

export const differentPoints = [
  {
    title: 'Actionable Structure',
    body: 'Each volume provides worksheets, matrices, checklists, and practical documentation tools.',
  },
  {
    title: 'One Connected Framework',
    body: 'All six volumes follow the same five-stage decision pathway.',
  },
  {
    title: 'Reusable Clinical Templates',
    body: 'Use the worksheets as organizational aids for appropriate cases.',
  },
  {
    title: 'Professional Flexibility',
    body: 'Adapt the tools to your clinical setting, applicable standards, and individual patient circumstances.',
  },
  {
    title: 'Documentation-Focused Design',
    body: 'Keep relevant findings, planning considerations, and follow-up information organized.',
  },
];

export const testimonials = [
  {
    name: 'Dr. Alejandro Vargas',
    role: 'General Dentist · Mexico',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/720f2aee-bdf5-42d0-a5e9-08d478d31def.jpg',
    quote:
      'I used to rebuild my retreatment notes from zero every single time. Now I open the intake worksheet, follow the five steps, and my reasoning is on one page. My referral letters are finally consistent.',
  },
  {
    name: 'Dra. Lucía Fernández',
    role: 'Endodontist · Spain',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e0bda133-75ab-48a6-9fc3-e60a8f00bc1c.jpg',
    quote:
      'The management options decision map is exactly what I needed for shared decision-making. Patients understand the alternatives better and my documentation is far more complete.',
  },
  {
    name: 'Dr. Ricardo Méndez',
    role: 'Restorative Dentist · Colombia',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a5e7fe98-6446-4741-8992-7efa83b926c0.jpg',
    quote:
      'After 25 years in practice I did not expect a $27 resource to change my workflow. The planning factor matrix keeps restorative and periodontal considerations from slipping through.',
  },
  {
    name: 'Dra. Valentina Ríos',
    role: 'General Dentist · Argentina',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1357b61d-51ff-4ab5-9e8e-b94762e4c6fe.jpg',
    quote:
      'As a younger clinician, the follow-up and audit checklists give me confidence that nothing is missing from my records. I print the quick card and keep it in my operatory.',
  },
];

export const faqs = [
  {
    q: 'Is this a traditional endodontics textbook?',
    a: 'No. The Endodontic Retreatment Decision Blueprint™ is organized around practical assessment tools, decision-support worksheets, clinical matrices, and documentation templates. It is designed to complement existing professional knowledge rather than replace formal clinical education.',
  },
  {
    q: 'Who is this resource designed for?',
    a: 'Qualified endodontists, general dentists involved in endodontic procedures, and dental professionals participating in appropriate treatment-planning activities.',
  },
  {
    q: 'Does this blueprint tell me exactly which treatment to perform?',
    a: 'No. It provides a structured framework for organizing relevant clinical information and documenting considerations associated with different management options. Treatment decisions must remain individualized and based on professional judgment, current evidence, patient circumstances, and applicable standards.',
  },
  {
    q: 'Can I use the worksheets with real clinical cases?',
    a: 'The templates are designed to support professional organization and documentation. They should be adapted to your practice requirements, local regulations, patient privacy obligations, and existing clinical protocols.',
  },
  {
    q: 'Does it include retreatment decision trees?',
    a: 'Yes. The collection includes structured decision maps, planning matrices, and checklists that help organize relevant considerations and potential management pathways. These tools support clinical reasoning; they do not independently determine diagnosis or treatment.',
  },
  {
    q: 'Are the materials available immediately?',
    a: 'Yes. The offer provides digital access after successful purchase.',
  },
  {
    q: 'Is this a subscription?',
    a: 'No. The complete digital package is offered as a one-time purchase of $27.',
  },
  {
    q: "What if the resource isn't suitable for me?",
    a: 'The offer includes a seven-day refund guarantee, subject to the published refund terms.',
  },
];

export const recentBuyers = [
  { name: 'Camila R.', country: 'Chile' },
  { name: 'Dr. Mateo G.', country: 'Mexico' },
  { name: 'Lucía F.', country: 'Spain' },
  { name: 'Dr. Santiago P.', country: 'Colombia' },
  { name: 'Valentina M.', country: 'Argentina' },
  { name: 'Dr. Diego H.', country: 'Peru' },
  { name: 'Sofía L.', country: 'Uruguay' },
  { name: 'Dr. Andrés C.', country: 'Ecuador' },
  { name: 'Isabella T.', country: 'Costa Rica' },
  { name: 'Dr. Javier N.', country: 'Guatemala' },
  { name: 'Mariana S.', country: 'Dominican Republic' },
  { name: 'Dr. Tomás V.', country: 'Paraguay' },
  { name: 'Daniela O.', country: 'Venezuela' },
  { name: 'Dr. Emilio R.', country: 'Bolivia' },
  { name: 'Gabriela A.', country: 'Panama' },
  { name: 'Dr. Nicolás B.', country: 'Puerto Rico' },
];
